import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@area-restrita/client';
import { MembershipDto } from '@area-restrita/api-client/model/membershipDto';
import { useUpdateMembership } from '@area-restrita/api-client/endpoints/memberships/memberships.gen';

import { Form } from '@/components/ui/form';

import { EditMemberForm, editMemberSchema } from './schema';
import EditMemberDialogView from './view';

interface IProps extends PropsWithChildren {
  membership: MembershipDto;
}

const EditMemberDialog: React.FC<IProps> = ({ children, membership }) => {
  const queryClient = useQueryClient();

  const defaultValues = useMemo<EditMemberForm>(
    () => ({
      externalId: membership.externalId ?? '',
      fullName: membership.user.fullName ?? '',
      email: membership.user.email,
    }),
    [membership],
  );

  const form = useForm<EditMemberForm>({
    defaultValues,
    resolver: zodResolver(editMemberSchema),
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const updateMembership = useUpdateMembership();

  const onSubmit = useCallback<SubmitHandler<EditMemberForm>>(
    async (data) => {
      try {
        await updateMembership.mutateAsync({
          userId: membership.user.id,
          data: {
            externalId: data.externalId,
            fullName: data.fullName,
          },
        });

        toast.success('Membro atualizado com sucesso!');
        await queryClient.invalidateQueries({
          queryKey: ['/api/v1/memberships'],
        });
      } catch (error) {
        const message =
          error instanceof ApiError && error.statusCode === 409
            ? error.message
            : 'Erro ao atualizar membro!';

        if (error instanceof ApiError && error.statusCode === 409) {
          form.setError('externalId', {
            type: 'manual',
            message,
          });
        }

        toast.error(message);
        throw error;
      }
    },
    [form, membership.user.id, queryClient, updateMembership],
  );

  return (
    <Form {...form}>
      <EditMemberDialogView
        control={form.control}
        onSubmit={(handleClose) =>
          form.handleSubmit(async (data) => {
            await onSubmit(data);
            handleClose();
          })
        }
        isLoading={form.formState.isSubmitting}
        onClose={() => form.reset(defaultValues)}
      >
        {children}
      </EditMemberDialogView>
    </Form>
  );
};

export default EditMemberDialog;
